import React, { useMemo, useEffect } from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  useOne,
  useTranslate,
} from '@pankod/refine-core';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Select,
  MenuItem,
  Typography,
  Backdrop,
  CircularProgress,
  Edit,
  InputLabel,
} from '@pankod/refine-mui';
import { useStepsForm } from '@pankod/refine-react-hook-form';
import { IOfferCodes } from 'interfaces';
import _ from 'lodash';
import { OfferExpiryType, PeriodOptions } from 'config/enum';

export const OfferCodeEdit: React.FC<
  IResourceComponentsProps
> = () => {
  const t = useTranslate();

  const {
    refineCore: { onFinish, formLoading, id },
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useStepsForm<IOfferCodes, HttpError, IOfferCodes & { period_key: string; }>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
  });
  const currentOffer = useOne<IOfferCodes>({ resource: 'Offers', id: id || '' });
  const expiryOfferType = currentOffer.data?.data?.type || OfferExpiryType.ByExpiryDate;
  const usageCount = currentOffer.data?.data?.use
    ? currentOffer.data?.data?.use
    : (
      currentOffer.data?.data?.device_using ? 1 : 0
    );

  const defaultPeriodKey = useMemo(() => {
    if (currentOffer.data?.data?.type === OfferExpiryType.ByPeriod) {
      const { data: currentOfferValue } = currentOffer.data;

      return `${currentOfferValue.period_type}_${currentOfferValue.period}`;
    }

    return undefined;
  }, [currentOffer.data?.data]);

  const onSubmit = async () => {
    const now = new Date();
    const _limit = String(getValues('limit'));

    const { period_key: selectedPeriod, ...submittedData } = getValues();
    const data = {
      ...submittedData,
      limit: parseInt(_limit || '1', 10),
      updated_at: now,
    };

    switch (expiryOfferType) {
      case OfferExpiryType.ByPeriod: {
        data.period = PeriodOptions[selectedPeriod].period;
        data.period_type = PeriodOptions[selectedPeriod].period_type;
        break;
      }
      default: {
        let expiryDate = getValues('expiry_date') as any;
        if (
          expiryDate
          && !(expiryDate instanceof Date)
          && typeof expiryDate === 'object'
          && 'seconds' in expiryDate
        ) {
          expiryDate = new Date(expiryDate.seconds * 1000);
        } else {
          expiryDate = new Date(expiryDate);
        }

        data.expiry_date = expiryDate;
        break;
      }
    }

    await onFinish(data as any);
  };

  const renderExpirySetting = () => {
    switch (expiryOfferType) {
      case OfferExpiryType.ByPeriod: {
        return (
          <FormControl>
            <InputLabel id="period_key">Period</InputLabel>

            <Select
              labelId="period_key"
              {...register('period_key', {
                required: t('errors.required.field', {
                  field: 'period_key',
                }),
              })}
              size="medium"
              variant="filled"
              label="Period"
              defaultValue={defaultPeriodKey}
              required
            >
              {Object.values(PeriodOptions)
                .map(
                  (item) => (
                    <MenuItem key={item.key} value={item.key}>
                      {item.label}
                    </MenuItem>
                  )
                )
              }
            </Select>
          </FormControl>
        )
      }
      default: {
        return (
          <FormControl>
            <TextField
              {...register('expiry_date', {
                required: t('errors.required.field', {
                  field: 'expiry_date',
                }),
              })}
              size="medium"
              variant="filled"
              required
              label="Expiry Date (required)"
              type="datetime-local"
            />
            {errors.expiry_date && (
              <FormHelperText error>
                {errors.expiry_date.message}
              </FormHelperText>
            )}
          </FormControl>
        )
      }
    }
  }

  const renderForm = () => {
    return (
      <>
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('name', {
                        required: t('errors.required.field', {
                          field: 'name',
                        }),
                        maxLength: {
                          value: 100,
                          message: t('errors.required.max', {
                            max: 100,
                          }),
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      label="Name (required)"
                    />
                    {errors.name && (
                      <FormHelperText error>
                        {errors.name.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <TextField
                      {...register('code', {
                        required: t('errors.required.field', {
                          field: 'code',
                        }),
                      })}
                      size="medium"
                      variant="filled"
                      required
                      label="Name (required)"
                      value={getValues('code')}
                    />
                    {errors.code && (
                      <FormHelperText error>
                        {errors.code.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {renderExpirySetting()}

                  <FormControl>
                    <TextField
                      {...register('limit', {
                        min: usageCount,
                        required: t('errors.required.field', {
                          field: 'limit',
                        }),
                      })}
                      size="medium"
                      variant="filled"
                      required
                      label={`Limit (Device usage count: ${usageCount})`}
                    />
                    {errors.code && (
                      <FormHelperText error>
                        {errors.code.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  useEffect(() => {
    if (defaultPeriodKey) {
      setValue('period_key', defaultPeriodKey);
    }
  }, [defaultPeriodKey]);

  return (
    <Edit
      title={<Typography variant="h5">Edit Offer Code</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: onSubmit }}
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        {renderForm()}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Edit>
  );
};
